import { getApiUrl } from "@/utils/api";
import { useState, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Activity, Eye, Users, CheckCircle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = getApiUrl();

function ActiveDot({ count }) {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      {count > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />}
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${count > 0 ? "bg-green-500" : "bg-gray-300"}`} />
    </span>
  );
}

function formatHour(h) {
  const hour = parseInt(h);
  if (hour === 0) return "12am";
  if (hour < 12) return `${hour}am`;
  if (hour === 12) return "12pm";
  return `${hour - 12}pm`;
}

export default function AnalyticsRealtime() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/analytics/realtime`, { withCredentials: true });
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 30000); // refresh every 30s
    return () => clearInterval(intervalRef.current);
  }, []);

  const hourlyData = (data?.hourly_data || []).map((h) => ({
    ...h,
    label: formatHour(h.hour.split(":")[0]),
  }));

  // Only show hours with data, or last 12 hours
  const now = new Date();
  const currentHour = now.getHours();
  const visibleHours = hourlyData.filter((_, i) => i <= currentHour);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const today = data?.today || {};

  return (
    <div className="space-y-6" data-testid="analytics-realtime">
      {/* Live indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ActiveDot count={data?.active_users || 0} />
          <span className="text-sm font-medium">Live Dashboard</span>
          <span className="text-xs text-muted-foreground">auto-refreshes every 30s</span>
        </div>
        <Button variant="outline" size="sm" className="rounded-none text-xs h-7" onClick={fetchData}>
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Active users banner */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6 text-center">
        <p className="text-5xl font-bold font-serif text-emerald-800">{data?.active_users ?? 0}</p>
        <p className="text-sm text-emerald-600 mt-1">active visitors right now</p>
        <p className="text-xs text-muted-foreground mt-0.5">(last 5 minutes)</p>
      </div>

      {/* Today's stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-border rounded-lg p-4 text-center">
          <Eye className="h-5 w-5 mx-auto text-blue-500 mb-2" />
          <p className="text-2xl font-bold font-serif text-blue-900">{(today.views || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Views Today</p>
        </div>
        <div className="bg-white border border-border rounded-lg p-4 text-center">
          <Users className="h-5 w-5 mx-auto text-primary mb-2" />
          <p className="text-2xl font-bold font-serif">{(today.sessions || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Sessions Today</p>
        </div>
        <div className="bg-white border border-border rounded-lg p-4 text-center">
          <CheckCircle className="h-5 w-5 mx-auto text-emerald-500 mb-2" />
          <p className="text-2xl font-bold font-serif text-emerald-900">{(today.conversions || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Conversions Today</p>
        </div>
      </div>

      {/* Hourly traffic */}
      <div className="bg-white border border-border rounded-lg p-5">
        <h3 className="font-serif text-sm font-medium mb-4">Hourly Traffic Today</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={visibleHours.length > 0 ? visibleHours : hourlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
            <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={1} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <div className="bg-white border border-border shadow-md p-2 rounded text-xs">
                    <p className="font-medium">{label}</p>
                    <p className="text-blue-600">{payload[0]?.value} views</p>
                  </div>
                ) : null
              }
            />
            <Bar dataKey="views" fill="#2f4f4f" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Active pages */}
      <div className="bg-white border border-border rounded-lg p-5">
        <h3 className="font-serif text-sm font-medium mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" /> Currently Active Pages
        </h3>
        {data?.active_pages?.length > 0 ? (
          <div className="space-y-2">
            {data.active_pages.map((page, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="text-xs font-medium truncate max-w-[280px]">{page.title || page.url}</p>
                  <p className="text-xs text-muted-foreground">{page.url}</p>
                </div>
                <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full ml-2 shrink-0">
                  {page.count} active
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">No active visitors right now</p>
        )}
      </div>
    </div>
  );
}
